/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action, StoreActionApi } from '@atlassian/react-sweet-state';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import type { TaskList, TaskId } from '../../common/types.tsx';
import type { State, ProjectState, HasEverSeenRoute } from '../model/types.tsx';
import { setIsOpen } from './checklist-open/index.tsx';
import { forceHideNudge } from './force-hide-nudge/index.tsx';
import { setHasEverSeenRoute } from './has-ever-seen-route/index.tsx';
import { addMountedNudge, removeMountedNudge } from './mounted-nudges/index.tsx';
import { nextAutoExpand } from './next-auto-expand/index.tsx';
import { nextSetChecklistIsDismissed } from './next-checklist-dismiss/index.tsx';
import { nextSetChecklistIsOpen } from './next-checklist-open/index.tsx';
import { nextMarkAsCompletedTask } from './next-complete-task/index.tsx';
import { nextOpenTask } from './next-open-task/index.tsx';
import { nextSetChecklistItemSeen } from './next-set-checklist-item-seen/index.tsx';
import { nextShouldShowNudgeAfterNavigation } from './next-should-show-nudge-after-navigation/index.tsx';
import {
	nextChangeTaskGroupParent as changeTaskGroupParent,
	nextGoBackToPreviousTaskGroup as goBackToPreviousTaskGroup,
} from './next-task-group/index.tsx';
import { nextGetTasks } from './next-tasks/index.tsx';
import { setNudgeStep } from './nudge-step/index.tsx';
import { openNextTask } from './open-next-task/index.tsx';
import { refreshAppStateUiStatusOnly } from './refresh-app-state-ui-status-only/index.tsx';
import { refreshAppState } from './refresh-app-state/index.tsx';
import { resetIsQuickstartLoaded } from './reset-is-quickstart-loaded/index.tsx';
import { setRoadmapIsCreateEnabled, setRoadmapIsListEmpty } from './roadmap-state/index.tsx';
import { setContainerType } from './set-container-type/index.tsx';
import { setHasNudgeActive } from './set-has-nudge-active/index.tsx';
import { setProjectState } from './set-project-state/index.tsx';
import {
	setQuickstartButtonRef,
	setIsOpenedViaButton,
	focusQuickstartButton,
} from './set-quickstart-button-ref/index.tsx';
import { setShouldRenderComponent } from './should-render-component/index.tsx';
import { syncVisibilityWithLocal } from './sync-visibility-with-local/index.tsx';
import type { Props } from './types.tsx';
import { universalDispatch } from './universal-dispatch/index.tsx';

// Note: Moving forward, stop adding new actions to this object.
// Instead, define any new actions needed in "./disconnected-actions" instead
// Then, at the call site, only use the universalDispatch action and pass the disconnected action.
// The future should look to refactor the actions object down to only the universalDispatch action.
export const actions: {
	changeTaskGroupParent: (newCurrentTaskGroupParent: string) => Action<State>;
	goBackToPreviousTaskGroup: () => Action<State>;
	setIsOpen: (isOpen: boolean) => Action<State>;
	refreshAppState: ({
		atlassianAccountId,
		cloudId,
		appEditions,
		isAdmin,
		canEditPlans,
		createAnalyticsEvent,
		projectType,
		containerType,
	}: {
		canEditPlans: boolean;
		createAnalyticsEvent: CreateUIAnalyticsEvent;
		projectType?: ProjectType;
		containerType?: 'project' | 'plan';
	} & Pick<TenantContext, 'cloudId' | 'atlassianAccountId' | 'appEditions' | 'isAdmin'>) => Action<
		State,
		Props
	>;
	setNudgeStep: (nudge: string) => Action<State>;
	addMountedNudge: (nudge: string) => Action<State>;
	removeMountedNudge: (nudge: string) => Action<State>;
	setShouldRenderComponent: (shouldRenderComponent: boolean) => Action<State>;
	setProjectState: (projectState: ProjectState) => Action<State>;
	setHasNudgeActive: (hasNudgeActive: boolean, itemName?: string | null) => Action<State>;
	forceHideNudge: () => Action<State>;
	setHasEverSeenRoute: (update: Partial<HasEverSeenRoute>) => Action<State>;
	setRoadmapIsCreateEnabled: (roadmapIsCreateEnabled: boolean) => Action<State>;
	setRoadmapIsListEmpty: (roadmapIsListEmpty: boolean) => Action<State>;
	openNextTask: () => Action<State>;
	resetIsQuickstartLoaded: () => Action<State>;
	refreshAppStateUiStatusOnly: ({
		taskList,
		containerType,
	}: {
		taskList: TaskList | undefined;
		containerType?: 'project' | 'plan';
	}) => Action<State, Props>;
	nextMarkAsCompletedTask: (taskId: TaskId) => Action<State>;
	nextSetChecklistItemSeen: (taskId: string) => Action<State>;
	nextSetChecklistIsDismissed: (isDismissed: boolean) => Action<State>;
	nextSetChecklistIsOpen: (isOpen: boolean) => Action<State>;
	nextOpenTask: (
		taskId: string,
		{
			closeNudge,
		}?: {
			closeNudge?: boolean;
		},
	) => Action<State>;
	nextAutoExpand: () => ({ getState, dispatch }: StoreActionApi<State>) => void;
	nextShouldShowNudgeAfterNavigation: (taskId: string) => Action<State>;
	nextGetTasks: (props: {
		projectState: ProjectState | null;
		taskGroupParent: string;
		taskList?: TaskList;
		isEligibleForTailoredOnboardingM2P5?: boolean;
		teamType?: string;
		features: {
			hasBacklog?: boolean;
			hasSprint?: boolean;
			hasSummaryPage?: boolean;
			hasRoadmap?: boolean;
			hasList?: boolean;
			hasCalendar?: boolean;
			hasCode?: boolean;
			hasForms?: boolean;
			hasPages?: boolean;
			hasPlanDependencies?: boolean;
			hasPlanProgram?: boolean;
			hasPlanReleases?: boolean;
			hasPlanTeams?: boolean;
		};
		permissions?: {
			canCreateIssue?: boolean;
		};
		containerType?: 'project' | 'plan';
	}) => Action<State>;
	syncVisibilityWithLocal: () => Action<State>;
	universalDispatch: ({ fn }: { fn: () => Action<State, void, void> }) => Action<State>;
	setContainerType: (containerType: 'project' | 'plan') => Action<State>;
	setQuickstartButtonRef: (quickstartButtonRef: HTMLElement | null) => Action<State>;
	setIsOpenedViaButton: (isOpenedViaButton: boolean) => Action<State>;
	focusQuickstartButton: () => Action<State>;
} = {
	changeTaskGroupParent,
	goBackToPreviousTaskGroup,
	setIsOpen,
	refreshAppState,
	setNudgeStep,
	addMountedNudge,
	removeMountedNudge,
	setShouldRenderComponent,
	setProjectState,
	setHasNudgeActive,
	forceHideNudge,
	setHasEverSeenRoute,
	setRoadmapIsCreateEnabled,
	setRoadmapIsListEmpty,
	openNextTask,
	resetIsQuickstartLoaded,
	refreshAppStateUiStatusOnly,
	nextMarkAsCompletedTask,
	nextSetChecklistItemSeen,
	nextSetChecklistIsDismissed,
	nextSetChecklistIsOpen,
	nextOpenTask,
	nextAutoExpand,
	nextShouldShowNudgeAfterNavigation,
	nextGetTasks,
	syncVisibilityWithLocal,
	universalDispatch,
	setContainerType,
	setQuickstartButtonRef,
	setIsOpenedViaButton,
	focusQuickstartButton,
};
